/**
 * Falling Pair System for Cell Swap Game
 * 
 * Manages falling cell pairs with rotation controls, collision detection,
 * and player input handling. Implements the core Puyo-style mechanics.
 */

import { Grid } from './Grid'
import { Cell, CellType, CellState, CellUtils } from './CellTypes'
import { GameBalanceManager } from './GameBalanceManager'

export enum RotationDirection {
  CLOCKWISE = 1,
  COUNTER_CLOCKWISE = -1
}

export enum MoveDirection {
  LEFT = -1,
  RIGHT = 1,
  DOWN = 1
}

export interface PairConfiguration {
  primary: CellType
  secondary: CellType
}

export class FallingPair {
  private primaryCell: Cell
  private secondaryCell: Cell
  private pivotX: number
  private pivotY: number
  private rotation: number // 0, 1, 2, 3 (0 = secondary above primary)
  private fallTimer: number
  private fallSpeed: number
  private grid: Grid
  private locked: boolean
  private lockDelayTimer: number | null  // null = not on ground, number = ms remaining
  private lockDelayDuration: number      // configurable grace period (ms)
  private onGround: boolean              // true when pair cannot move down
  private gameMode: 'puyo' | 'medical' = 'puyo'

  constructor(config: PairConfiguration, grid: Grid, startX: number = 2, gameMode: 'puyo' | 'medical' = 'puyo') {
    this.grid = grid
    this.gameMode = gameMode
    this.pivotX = startX
    this.pivotY = 1 // Start at row 1 so secondary cell at row 0 is still in bounds
    this.rotation = 0
    this.fallTimer = 0
    this.fallSpeed = 1000 // milliseconds per fall step
    this.locked = false
    this.lockDelayTimer = null
    this.lockDelayDuration = 500  // 500ms grace period on ground contact
    this.onGround = false

    // Create the two cells - positions will be set by updateCellPositions()
    this.primaryCell = CellUtils.createCell(config.primary, startX, 1)
    this.secondaryCell = CellUtils.createCell(config.secondary, startX, 0)

    this.primaryCell.state = CellState.FALLING
    this.secondaryCell.state = CellState.FALLING

    this.updateCellPositions()
  }

  /**
   * Updates the falling pair based on elapsed time.
   * Implements a lock delay: when the pair touches the ground it starts a
   * grace-period timer instead of locking immediately. The player can still
   * move/rotate during the delay. The timer resets on any successful
   * move or rotation. Locking happens only when the timer expires.
   */
  public update(deltaTime: number): boolean {
    if (this.locked) {
      return false
    }

    // --- Lock-delay handling ---
    if (this.onGround) {
      // Re-check: maybe the player moved the pair to a position where it
      // is no longer on the ground (e.g. slid off a ledge).
      if (this.canMoveTo(this.pivotX, this.pivotY + 1, this.rotation)) {
        // No longer on ground — cancel the lock delay and resume falling
        this.onGround = false
        this.lockDelayTimer = null
      } else {
        // Still on ground — tick the lock-delay timer
        this.lockDelayTimer = (this.lockDelayTimer ?? this.lockDelayDuration) - deltaTime
        if (this.lockDelayTimer <= 0) {
          this.lockPair()
          return false
        }
        // Pair is waiting on ground but not yet locked
        return true
      }
    }

    // --- Normal gravity fall ---
    this.fallTimer += deltaTime

    if (this.fallTimer >= this.fallSpeed) {
      this.fallTimer = 0
      return this.moveDown()
    }

    return true
  }

  /**
   * Attempts to move the pair left or right
   */
  public moveHorizontal(direction: MoveDirection): boolean {
    if (this.locked) {
      return false
    }

    const newPivotX = this.pivotX + direction

    if (this.canMoveTo(newPivotX, this.pivotY, this.rotation)) {
      this.pivotX = newPivotX
      this.updateCellPositions()
      // Reset lock delay on successful horizontal move
      if (this.onGround) {
        this.lockDelayTimer = this.lockDelayDuration
      }
      return true
    }

    return false
  }

  /**
   * Attempts to move the pair down (soft drop).
   * If the pair cannot move down, it enters the lock-delay state
   * rather than locking immediately.
   */
  public moveDown(): boolean {
    if (this.locked) {
      return false
    }

    const newPivotY = this.pivotY + 1

    if (this.canMoveTo(this.pivotX, newPivotY, this.rotation)) {
      this.pivotY = newPivotY
      this.updateCellPositions()
      // Successfully moved down — reset lock delay if it was active
      this.onGround = false
      this.lockDelayTimer = null
      return true
    } else {
      // Cannot move down — enter lock-delay state (do NOT lock immediately)
      if (!this.onGround) {
        this.onGround = true
        this.lockDelayTimer = this.lockDelayDuration
      }
      // Return true: the pair is still "alive" (player can move/rotate)
      return true
    }
  }

  /**
   * Hard drop - instantly moves the pair to the bottom
   */
  public hardDrop(): number {
    if (this.locked) {
      return 0
    }

    let dropDistance = 0

    while (this.canMoveTo(this.pivotX, this.pivotY + 1, this.rotation)) {
      this.pivotY++
      dropDistance++
    }

    this.updateCellPositions()
    this.lockPair()

    return dropDistance
  }

  /**
   * Attempts to rotate the pair
   */
  public rotate(direction: RotationDirection): boolean {
    if (this.locked) {
      return false
    }

    const newRotation = (this.rotation + direction + 4) % 4

    // Try normal rotation
    if (this.canMoveTo(this.pivotX, this.pivotY, newRotation)) {
      this.rotation = newRotation
      this.updateCellPositions()
      // Reset lock delay on successful rotation
      if (this.onGround) {
        this.lockDelayTimer = this.lockDelayDuration
      }
      return true
    }

    // Try wall kick - move left or right to allow rotation
    const wallKickOffsets = [-1, 1, -2, 2]

    for (const offset of wallKickOffsets) {
      const kickX = this.pivotX + offset

      if (this.canMoveTo(kickX, this.pivotY, newRotation)) {
        this.pivotX = kickX
        this.rotation = newRotation
        this.updateCellPositions()
        // Reset lock delay on successful wall-kick rotation
        if (this.onGround) {
          this.lockDelayTimer = this.lockDelayDuration
        }
        return true
      }
    }

    return false
  }

  /**
   * Sets the fall speed (lower = faster)
   */
  public setFallSpeed(speed: number): void {
    this.fallSpeed = Math.max(50, speed) // Minimum 50ms
  }

  /**
   * Checks if the pair can move to a new position with rotation
   */
  private canMoveTo(pivotX: number, pivotY: number, rotation: number): boolean {
    const positions = this.getRotatedPositions(pivotX, pivotY, rotation)

    for (const pos of positions) {
      // Check bounds
      if (!this.grid.isValidPosition(pos.x, pos.y)) {
        return false
      }

      // Check collision with existing cells
      if (!this.grid.isEmpty(pos.x, pos.y)) {
        return false
      }
    }

    return true
  }

  /**
   * Gets the cell positions for a given pivot and rotation
   */
  private getRotatedPositions(pivotX: number, pivotY: number, rotation: number): { x: number, y: number }[] {
    const positions: { x: number, y: number }[] = []

    // Primary cell is always at the pivot
    positions.push({ x: pivotX, y: pivotY })

    // Secondary cell position depends on rotation
    switch (rotation) {
      case 0: // Secondary above primary
        positions.push({ x: pivotX, y: pivotY - 1 })
        break
      case 1: // Secondary to the right of primary
        positions.push({ x: pivotX + 1, y: pivotY })
        break
      case 2: // Secondary below primary
        positions.push({ x: pivotX, y: pivotY + 1 })
        break
      case 3: // Secondary to the left of primary
        positions.push({ x: pivotX - 1, y: pivotY })
        break
    }

    return positions
  }

  /**
   * Updates the actual cell positions based on pivot and rotation
   */
  private updateCellPositions(): void {
    const positions = this.getRotatedPositions(this.pivotX, this.pivotY, this.rotation)

    this.primaryCell.x = positions[0].x
    this.primaryCell.y = positions[0].y

    this.secondaryCell.x = positions[1].x
    this.secondaryCell.y = positions[1].y
  }

  /**
   * Locks the pair in place and adds cells to the grid
   * Behavior differs based on game mode:
   * - Puyo mode: Applies separation physics (cells fall independently)
   * - Medical mode: Establishes capsule connection (cells stay connected)
   */
  private lockPair(): void {
    this.locked = true

    this.primaryCell.state = CellState.IDLE
    this.secondaryCell.state = CellState.IDLE

    // Place both cells initially
    this.grid.placeCell(this.primaryCell, this.primaryCell.x, this.primaryCell.y)
    this.grid.placeCell(this.secondaryCell, this.secondaryCell.x, this.secondaryCell.y)

    // Apply mode-specific behavior
    if (this.gameMode === 'medical') {
      // Dr. Mario mode: Establish capsule connection
      this.establishCapsuleConnection()
    } else {
      // Puyo mode: Apply separation physics
      this.applySeparationPhysics()
    }
  }

  /**
   * Establishes a capsule connection between the two cells (Dr. Mario mode)
   * The cells will remain connected and move together as a unit
   */
  private establishCapsuleConnection(): void {
    // Generate a unique capsule pair ID
    const capsulePairId = `capsule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Link the cells together
    this.primaryCell.capsulePairId = capsulePairId
    this.primaryCell.capsulePartner = this.secondaryCell.id

    this.secondaryCell.capsulePairId = capsulePairId
    this.secondaryCell.capsulePartner = this.primaryCell.id
  }

  /**
   * Applies Puyo-style separation physics where unsupported cells fall independently
   */
  private applySeparationPhysics(): void {
    const cellsToCheck = [this.primaryCell, this.secondaryCell]

    for (const cell of cellsToCheck) {
      // Check if this cell has support underneath it
      if (!this.hasSupportUnderneath(cell)) {
        // Remove the cell from its current position
        this.grid.removeCell(cell.x, cell.y)

        // Find the lowest position this cell can fall to
        const newY = this.findLowestPosition(cell.x, cell.y)

        // Place the cell at its new position
        cell.y = newY
        cell.state = CellState.FALLING
        this.grid.placeCell(cell, cell.x, newY)
      }
    }
  }

  /**
   * Checks if a cell has solid support underneath it
   */
  private hasSupportUnderneath(cell: Cell): boolean {
    // If the cell is at the bottom of the grid, it has support
    if (cell.y >= Grid.HEIGHT - 1) {
      return true
    }

    // Check if there's a cell directly underneath
    const cellBelow = this.grid.getCell(cell.x, cell.y + 1)
    return cellBelow !== null
  }

  /**
   * Finds the lowest position a cell can fall to
   */
  private findLowestPosition(x: number, startY: number): number {
    for (let y = startY + 1; y < Grid.HEIGHT; y++) {
      if (!this.grid.isEmpty(x, y)) {
        return y - 1 // Return the position just above the obstacle
      }
    }
    return Grid.HEIGHT - 1 // Fall to the bottom
  }

  /**
   * Gets the current cells in the pair
   */
  public getCells(): { primary: Cell, secondary: Cell } {
    return {
      primary: this.primaryCell,
      secondary: this.secondaryCell
    }
  }

  /**
   * Gets the current position and rotation
   */
  public getState(): { x: number, y: number, rotation: number, locked: boolean } {
    return {
      x: this.pivotX,
      y: this.pivotY,
      rotation: this.rotation,
      locked: this.locked
    }
  }

  /**
   * Returns true if the pair is locked in place.
   */
  public isLocked(): boolean {
    return this.locked
  }

  /**
   * Returns true if the pair is sitting on the ground but not yet locked
   * (lock delay is active). Useful for UI feedback (e.g. flashing sprite).
   */
  public isOnGround(): boolean {
    return this.onGround && !this.locked
  }

  /**
   * Returns the remaining lock-delay time as a 0-1 fraction (1 = just landed, 0 = about to lock).
   * Returns null if not in lock-delay state.
   */
  public getLockDelayProgress(): number | null {
    if (!this.onGround || this.locked || this.lockDelayTimer === null) return null
    return Math.max(0, this.lockDelayTimer / this.lockDelayDuration)
  }

  /**
   * Gets the current fall progress (0-1)
   */
  public getFallProgress(): number {
    return this.fallTimer / this.fallSpeed
  }
}

/**
 * Generator for creating falling pairs with appropriate cell type distributions
 */
export class PairGenerator {
  private cellTypeWeights: Map<CellType, number>
  private currentBag: CellType[]
  private bagIndex: number
  private balanceManager?: GameBalanceManager
  private lastPair?: PairConfiguration
  private gameMode: 'puyo' | 'medical' = 'puyo' // Default to Puyo mode

  constructor(balanceManager?: GameBalanceManager) {
    this.balanceManager = balanceManager
    this.cellTypeWeights = new Map([
      [CellType.RED, 20],       // Equal probability
      [CellType.BLUE, 20],      // Equal probability
      [CellType.GREEN, 20],     // Equal probability  
      [CellType.YELLOW, 20],    // Equal probability
      [CellType.PURPLE, 20],    // Equal probability
      [CellType.PLATELETS, 20]  // Equal probability
    ])

    this.currentBag = []
    this.bagIndex = 0
    this.lastPair = undefined
    this.gameMode = 'puyo'
    this.generateNewBag()
  }

  /**
   * Generates the next falling pair
   */
  public generateNext(): PairConfiguration {
    let attempts = 0
    const maxAttempts = 10
    let primary: CellType
    let secondary: CellType

    // Try to avoid identical consecutive pairs (same colors in same order)
    do {
      if (this.bagIndex >= this.currentBag.length - 1) {
        this.generateNewBag()
        this.bagIndex = 0
      }

      primary = this.currentBag[this.bagIndex]
      secondary = this.currentBag[this.bagIndex + 1]

      // Check if this pair is identical to the last one
      const isIdentical = this.lastPair &&
        this.lastPair.primary === primary &&
        this.lastPair.secondary === secondary

      if (!isIdentical || attempts >= maxAttempts) {
        // Accept this pair
        this.bagIndex += 2
        break
      }

      // Skip this pair and try the next one
      this.bagIndex += 2
      attempts++
    } while (attempts < maxAttempts)

    const pair: PairConfiguration = { primary, secondary }
    this.lastPair = pair
    return pair
  }

  /**
   * Generates a preview of upcoming pairs
   */
  public previewNext(count: number = 3): PairConfiguration[] {
    const preview: PairConfiguration[] = []
    let tempBag = [...this.currentBag]
    let tempIndex = this.bagIndex

    for (let i = 0; i < count; i++) {
      if (tempIndex >= tempBag.length - 1) {
        tempBag = this.createBag()
        tempIndex = 0
      }

      const primary = tempBag[tempIndex++]
      const secondary = tempBag[tempIndex++]
      preview.push({ primary, secondary })
    }

    return preview
  }

  /**
   * Set the game mode to determine which cell types to use
   */
  public setGameMode(mode: 'puyo' | 'medical'): void {
    this.gameMode = mode
    // Update weights immediately based on mode
    if (mode === 'medical') {
      this.cellTypeWeights.clear()
      this.cellTypeWeights.set(CellType.RBC, 30)     // Red Blood Cells
      this.cellTypeWeights.set(CellType.WBC, 30)     // White Blood Cells  
      this.cellTypeWeights.set(CellType.DONOR, 30)   // Donor Cells
      this.cellTypeWeights.set(CellType.STEM, 10)    // Stem Cells (wildcard, rare)
      // Note: Cancer cells are NOT included
    } else {
      this.cellTypeWeights.clear()
      this.cellTypeWeights.set(CellType.RED, 20)
      this.cellTypeWeights.set(CellType.BLUE, 20)
      this.cellTypeWeights.set(CellType.GREEN, 20)
      this.cellTypeWeights.set(CellType.YELLOW, 20)
      this.cellTypeWeights.set(CellType.PURPLE, 20)
      this.cellTypeWeights.set(CellType.PLATELETS, 20)
    }
    // Regenerate the bag to use the new weights
    this.generateNewBag()
  }

  /**
   * Adjusts cell type weights based on game state and mode
   */
  public adjustWeights(cancerCount: number, level: number): void {
    if (this.gameMode === 'medical') {
      // Medical mode for Puzzle game: use medical cell types (4 colors)
      this.cellTypeWeights.clear()
      this.cellTypeWeights.set(CellType.RBC, 30)     // Red Blood Cells
      this.cellTypeWeights.set(CellType.WBC, 30)     // White Blood Cells  
      this.cellTypeWeights.set(CellType.DONOR, 30)   // Donor Cells
      this.cellTypeWeights.set(CellType.STEM, 10)    // Stem Cells (wildcard, rare)
      // Note: Cancer cells are NOT included in falling pairs
    } else {
      // Puyo mode for other games: use Puyo colors with equal probability including PLATELETS
      this.cellTypeWeights.clear()
      this.cellTypeWeights.set(CellType.RED, 20)
      this.cellTypeWeights.set(CellType.BLUE, 20)
      this.cellTypeWeights.set(CellType.GREEN, 20)
      this.cellTypeWeights.set(CellType.YELLOW, 20)
      this.cellTypeWeights.set(CellType.PURPLE, 20)
      this.cellTypeWeights.set(CellType.PLATELETS, 20)
    }
  }

  /**
   * Generates a new bag of cell types
   */
  private generateNewBag(): void {
    this.currentBag = this.createBag()
    this.shuffleBag()
  }

  /**
   * Creates a weighted bag of cell types
   */
  private createBag(): CellType[] {
    const bag: CellType[] = []

    for (const [cellType, weight] of this.cellTypeWeights) {
      for (let i = 0; i < weight; i++) {
        bag.push(cellType)
      }
    }

    return bag
  }

  /**
   * Shuffles the current bag using Fisher-Yates algorithm
   */
  private shuffleBag(): void {
    for (let i = this.currentBag.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
        ;[this.currentBag[i], this.currentBag[j]] = [this.currentBag[j], this.currentBag[i]]
    }
  }
}